import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2, ShieldAlert, RefreshCw, Webhook, ShieldCheck, ShieldX } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchAdminWebhooks } from "../lib/admin";

const ESTADO_STYLE = {
  procesado: "bg-emerald-50 text-emerald-700",
  recibido: "bg-sky-50 text-sky-700",
  duplicado: "bg-slate-100 text-slate-500",
  ignorado: "bg-slate-100 text-slate-500",
  firma_rechazada: "bg-red-50 text-brand-red",
  error: "bg-orange-50 text-orange-700",
};

const ESTADO_LABEL = {
  procesado: "Procesado",
  recibido: "Recibido",
  duplicado: "Duplicado",
  ignorado: "Ignorado",
  firma_rechazada: "Firma rechazada",
  error: "Error",
};

function fmtFecha(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-PE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function EstadoBadge({ estado }) {
  return (
    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${ESTADO_STYLE[estado] || "bg-slate-100 text-slate-500"}`} data-testid={`wh-estado-${estado}`}>
      {ESTADO_LABEL[estado] || estado}
    </span>
  );
}

export default function AdminWebhooksPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, resumen: {} });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");
  const [estado, setEstado] = useState("");

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminWebhooks({ source, estado, limite: 100 });
      setData(res);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [source, estado]);

  useEffect(() => { cargar(); }, [cargar]);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="admin-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <p className="mt-2 text-slate-500">Esta sección es exclusiva para administradores.</p>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  const resumen = data.resumen || {};

  return (
    <div className="px-5 md:px-8 py-8" data-testid="admin-webhooks-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver al Panel
      </Link>
      <header className="mt-4 mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-blue">Pagos · Mercado Pago</p>
          <h1 className="mt-1 text-3xl font-extrabold text-slate-900 tracking-tight">Historial de Webhooks</h1>
          <p className="mt-1 text-slate-500 text-sm">Auditoría de cada notificación recibida: firma, estado de procesamiento y errores.</p>
        </div>
        <button data-testid="wh-refresh" onClick={cargar} className="shrink-0 inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
          <RefreshCw size={16} /> Actualizar
        </button>
      </header>

      {/* KPIs por estado */}
      <div className="mb-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {["procesado", "recibido", "duplicado", "ignorado", "firma_rechazada", "error"].map((e) => (
          <div key={e} className="rounded-xl border border-slate-100 bg-white px-4 py-3" data-testid={`wh-kpi-${e}`}>
            <p className="text-2xl font-extrabold text-slate-900 leading-none">{resumen[e] || 0}</p>
            <p className="text-[11px] text-slate-500 mt-1">{ESTADO_LABEL[e]}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <select data-testid="wh-filter-source" value={source} onChange={(e) => setSource(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-200 text-sm text-slate-600">
          <option value="">Todos los orígenes</option>
          <option value="pagos">Pagos (membresías/destacar/software/academia)</option>
          <option value="marketplace">Marketplace</option>
        </select>
        <select data-testid="wh-filter-estado" value={estado} onChange={(e) => setEstado(e.target.value)} className="h-11 px-3 rounded-xl border border-slate-200 text-sm text-slate-600">
          <option value="">Todos los estados</option>
          <option value="procesado">Procesado</option>
          <option value="recibido">Recibido</option>
          <option value="duplicado">Duplicado</option>
          <option value="ignorado">Ignorado</option>
          <option value="firma_rechazada">Firma rechazada</option>
          <option value="error">Error</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando webhooks…</div>
        ) : data.items.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm" data-testid="wh-empty">
            <Webhook size={32} className="mx-auto mb-2 text-slate-300" />
            Aún no se han recibido webhooks de Mercado Pago.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="wh-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="py-3 px-4">Fecha</th>
                  <th className="py-3 px-2">Origen</th>
                  <th className="py-3 px-2">Payment ID</th>
                  <th className="py-3 px-2">Firma</th>
                  <th className="py-3 px-2">Estado</th>
                  <th className="py-3 px-2">MP</th>
                  <th className="py-3 px-4">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((w) => (
                  <tr key={w.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`wh-row-${w.id}`}>
                    <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">{fmtFecha(w.created_at)}</td>
                    <td className="py-3 px-2 text-slate-600 capitalize">{w.source || "—"}</td>
                    <td className="py-3 px-2 font-mono text-xs text-slate-700">{w.payment_id || "—"}</td>
                    <td className="py-3 px-2">
                      {w.signature_valid ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><ShieldCheck size={13} /> {w.signature_reason}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-brand-red text-xs font-semibold"><ShieldX size={13} /> {w.signature_reason}</span>
                      )}
                    </td>
                    <td className="py-3 px-2"><EstadoBadge estado={w.processing_status} /></td>
                    <td className="py-3 px-2 text-slate-500 text-xs">{w.mp_status || "—"}</td>
                    <td className="py-3 px-4 text-xs text-slate-500 max-w-[220px] truncate">
                      {w.error ? <span className="text-orange-700">{w.error}</span> : (w.external_reference || "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-400">{data.total} webhook(s) registrados.</p>
    </div>
  );
}
